import axios from 'axios'
import queryString from 'query-string'

import { PaginatedResults, SnakeToCamelCaseNested } from '../helpers/typeHelper'
import { toCamelCase } from '../helpers/utils'
import { ApiVersion } from '.'
import { UserStatusHistory } from './userTimeline'

export type NoteRecordType = 'users' | 'jobs' | 'teams' | 'console/employment-history' | 'console/teams'

export const RecordInlineLabel: Record<NoteRecordType, string> = {
  users: 'driver',
  jobs: 'job',
  'console/employment-history': 'Employment job',
  teams: 'team',
  'console/teams': 'team',
}

export const RecordHeaderLabel: Record<NoteRecordType, string> = {
  users: 'Driver',
  jobs: 'Job',
  'console/employment-history': 'Employment Job',
  teams: 'Fleet',
  'console/teams': 'Fleet',
}

export interface NoteUser {
  firstName: string
  email: string
  id: string
  lastName: string
}

export interface NoteTagWithLabel {
  label: string
  value: string
}

export interface Note {
  dateCreated: string
  dateUpdated: string
  createdBy: NoteUser
  text: string
  id: string
  relatedObjectContentType: string
  relatedObjectId: string
  isEdited: boolean
  tags: string[]
  tagsWithLabels: NoteTagWithLabel[]
}

export type Activity = SnakeToCamelCaseNested<UserStatusHistory>

export function isNote(note: Note | Activity): note is Note {
  return 'text' in note
}

// API

interface GetNotes {
  recordType: NoteRecordType
  recordId: string
  cursor?: string
  tags?: string[] | null
  apiVersion?: ApiVersion
  nextPageParamName?: 'page' | 'cursor'
}

export function getNotes({
  recordType,
  recordId,
  nextPageParamName = 'cursor',
  cursor,
  tags,
  apiVersion = 'v3',
}: GetNotes): Promise<PaginatedResults<Note>> {
  const params = {
    tags: tags?.join(',') || '',
    [nextPageParamName]: cursor || nextPageParamName === 'page' ? '1' : '',
  }

  const queryParams = queryString.stringify(params)

  return axios.get(`/api/${apiVersion}/${recordType}/${recordId}/notes/?${queryParams}`).then((res) => ({
    ...res.data,
    results: res.data.results.map(toCamelCase),
  }))
}

interface GetTimeline {
  recordType: NoteRecordType
  recordId: string
  filterStatusHistoryOnly?: boolean
  tags?: string[] | null
}

export interface GetTimelineResult {
  totalCount: number
  suspensionCount: number
  results?: Note[] | Activity[]
}

export type NoteOrActivity = Note | Activity

export function getTimeline({
  recordType,
  recordId,
  filterStatusHistoryOnly,
  tags,
}: GetTimeline): Promise<GetTimelineResult> {
  return axios
    .get(
      `/api/v3/${recordType}/${recordId}/timeline/?filter_status_history_only=${
        filterStatusHistoryOnly || ''
      }&tags=${tags?.join(',') || ''}`
    )
    .then((res) => ({
      totalCount: res.data.total_count,
      suspensionCount: res.data.suspension_count,
      results: res.data.results.map(toCamelCase),
    }))
}

interface CreateNotePayload {
  payload: {
    text: string
    tags?: string[]
  }
}

export function createNote({
  recordType,
  recordId,
  payload,
  apiVersion = 'v3',
}: GetNotes & CreateNotePayload): Promise<Note> {
  return axios
    .post(`/api/${apiVersion}/${recordType}/${recordId}/notes/`, payload)
    .then((res) => toCamelCase(res.data) as Note)
}

export function updateNote({
  recordType,
  recordId,
  noteId,
  payload,
  apiVersion = 'v3',
}: GetNotes & CreateNotePayload & { noteId: string }): Promise<Note> {
  return axios
    .put(`/api/${apiVersion}/${recordType}/${recordId}/notes/${noteId}/`, payload)
    .then((res) => toCamelCase(res.data) as Note)
}

export function deleteNote({
  recordType,
  recordId,
  noteId,
  apiVersion = 'v3',
}: GetNotes & { noteId: string }): Promise<Note> {
  return axios
    .delete(`/api/${apiVersion}/${recordType}/${recordId}/notes/${noteId}/`)
    .then((res) => toCamelCase(res.data) as Note)
}

export interface NotesAvailabelTag {
  value: string
  label: string
}

export function getAvailableTags({
  recordType,
  recordId,
  apiVersion = 'v3',
}: GetNotes): Promise<{ tags: NotesAvailabelTag[] }> {
  return axios
    .get(`/api/${apiVersion}/${recordType}/${recordId}/notes/available-tags/`)
    .then((res) => toCamelCase(res.data) as { tags: NotesAvailabelTag[] })
}

export interface NotesAddedTag {
  value: string
  label: string
  count: number
}

export function getAddedTags({
  recordType,
  recordId,
  apiVersion = 'v3',
}: GetNotes): Promise<NotesAddedTag[]> {
  return axios
    .get(`/api/${apiVersion}/${recordType}/${recordId}/notes/added-tags/`)
    .then((res) => toCamelCase(res.data) as NotesAddedTag[])
}
